import React, { forwardRef } from "react";
import { makeStyles, lighten } from "@material-ui/core/styles";
import ListItem, { ListItemProps } from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText, {
  ListItemTextProps,
} from "@material-ui/core/ListItemText";
import Tooltip from "@material-ui/core/Tooltip";
import { LinkProps } from "react-router-dom";
import clsx from "clsx";

import { AdapterLink } from "src/utils";
import type { Theme } from "src/theme";
import { getContrastRatio } from "src/utils/colorUtils";

export interface NavigatorItemProps {
  Icon: React.ComponentType;
  primary: ListItemTextProps["primary"];
  to?: LinkProps["to"];
  active?: boolean;
  collapsed?: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === "dark";
  const { custom: palette } = theme.palette.custom;

  // Get colors from theme
  const accentColor = palette.accent;
  const brandPrimary = palette.brandPrimary;
  const brandSecondary = palette.brandSecondary;
  const cardColor = palette.card;

  // In light mode, use brandPrimary; in dark mode, use accent with contrast check
  let baseMenuColor: string;
  if (isDark) {
    const contrastRatio = getContrastRatio(accentColor, cardColor);
    baseMenuColor =
      contrastRatio >= 3 ? accentColor : theme.palette.text.primary;
  } else {
    const contrastRatio = getContrastRatio(brandPrimary, cardColor);
    baseMenuColor =
      contrastRatio >= 3 ? brandPrimary : theme.palette.text.primary;
  }

  // Derive menu text color - lighter in dark mode for readability
  const menuTextColor = isDark ? lighten(baseMenuColor, 0.35) : baseMenuColor;

  const menuHoverColor = theme.palette.action.hover;

  // Active highlight colors based on brandSecondary
  const activeColor = brandSecondary;
  const activeHoverColor = lighten(brandSecondary, 0.2);

  return {
    root: {
      transition:
        "transform 0.2s ease, background-color 0.2s ease, color 0.2s ease",
      "&:hover": {
        backgroundColor: menuHoverColor,
        borderRadius: theme.shape.borderRadius,
        transform: "translateX(4px)",
      },
      color: menuTextColor,
    },
    active: {
      "&:hover": {
        backgroundColor: activeHoverColor,
      },
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.getContrastText(activeColor),
      backgroundColor: activeColor,
    },
    collapsed: {
      justifyContent: "center",
      padding: theme.spacing(1),
      margin: "4px auto",
      width: 48,
      height: 48,
      borderRadius: "50%",
      minHeight: "auto",
      "&:hover": {
        backgroundColor: "transparent",
        transform: "scale(1.1)",
        color: isDark
          ? lighten(baseMenuColor, 0.45)
          : lighten(baseMenuColor, 0.2),
      },
    },
    collapsedActive: {
      borderRadius: "50%",
      "&:hover": {
        backgroundColor: activeHoverColor,
        color: theme.palette.getContrastText(activeHoverColor),
        transform: "scale(1.1)",
      },
    },
    iconCollapsed: {
      minWidth: "auto",
      "& svg": {
        fontSize: 28,
      },
    },
  };
});

function NavigatorItemRender(
  {
    Icon,
    primary,
    active = false,
    collapsed = false,
    className,
    ...props
  }: NavigatorItemProps & Omit<ListItemProps, "button" | "dense" | "component">,
  ref
) {
  const classes = useStyles();

  const listItem = (
    <ListItem
      ref={ref}
      button
      dense
      className={clsx(
        className,
        classes.root,
        active && classes.active,
        collapsed && classes.collapsed,
        collapsed && active && classes.collapsedActive
      )}
      {...(props.to ? { component: AdapterLink } : undefined)}
      {...(props as any)}
    >
      <ListItemIcon className={clsx(collapsed && classes.iconCollapsed)}>
        <Icon />
      </ListItemIcon>
      {!collapsed && <ListItemText primary={primary} />}
    </ListItem>
  );

  if (collapsed) {
    return (
      <Tooltip title={primary as string} placement="right">
        {listItem}
      </Tooltip>
    );
  }

  return listItem;
}

const NavigatorItem = forwardRef(NavigatorItemRender);

export default NavigatorItem;
